import type { OpenInterval } from "@suse-o11y/date-time/open-interval";
import type { Time } from "@suse-o11y/date-time/time";
import { round } from "@suse-o11y/round";
import { sortBy } from "@suse-o11y/sort-by";
import { timeToPx } from "@suse-o11y/time-to-px";
import { toArray } from "@suse-o11y/to-array";

import { SegmentEdge } from "./segment";

export interface MutedSegment {
	leftPx: number;
	rightPx: number;
}

interface GetMutedSegmentsParams {
	intervals: ReadonlyArray<OpenInterval>;
	timelineStart: Time;
	timelineEnd: Time;
	timelineWidth: number;
	gap: number;
	minWidth: number;
}

function mergeMutedSegments(
	{ gap, minWidth }: Pick<GetMutedSegmentsParams, "gap" | "minWidth">,
	current: MutedSegment,
	next: MutedSegment,
): ReadonlyArray<MutedSegment> {
	return next.rightPx >= current.leftPx - gap - minWidth
		? [{ leftPx: next.leftPx, rightPx: current.rightPx }]
		: [next, current];
}

export function getMutedSegments({
	intervals,
	timelineStart,
	timelineEnd,
	timelineWidth,
	gap,
	minWidth,
}: GetMutedSegmentsParams): Array<MutedSegment> {
	return toArray(
		sortBy(intervals, [([start]) => start, "desc"]).reduce(
			(builder, [start, end = timelineEnd]) =>
				builder.shift({
					leftPx: round(
						timeToPx({
							time: start,
							width: timelineWidth,
							interval: { start: timelineStart, end: timelineEnd },
						}),
						0,
					),
					rightPx: round(
						timeToPx({
							time: end,
							width: timelineWidth,
							interval: { start: timelineStart, end: timelineEnd },
						}),
						0,
					),
				}),
			new SegmentEdge<MutedSegment>((current, next) => mergeMutedSegments({ gap, minWidth }, current, next)),
		),
	);
}
